import React, { ReactNode, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { LogOut, FileText, Users, Settings, Home, FolderOpen, BarChart3, UserCog, AlertCircle, Banknote, ChevronLeft, ChevronRight, StickyNote } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';


interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  icon: any;
  label: string;
  roles: string[];
  clientRoles?: string[];
  disabled?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  
  const isClientAdmin = user?.clientRole === 'lab_owner' || user?.clientRole === 'client_admin';

  const navItems: NavItem[] = [
    { path: '/', icon: Home, label: 'Inicio', roles: ['admin', 'client'] },
    { path: '/clients', icon: Users, label: 'Clientes', roles: ['admin'] },
    { path: '/invoices', icon: FileText, label: 'Facturas', roles: ['admin', 'client'] },
    { path: '/cheques', icon: Banknote, label: 'Cheques', roles: ['admin'] },
    { path: '/my-cheques', icon: Banknote, label: 'Cheques', roles: ['client'] },
    { path: '/my-reports', icon: BarChart3, label: 'Mis Reportes', roles: ['client'], clientRoles: ['lab_owner', 'client_admin'] },
    { path: '/my-documents', icon: FolderOpen, label: 'Mis Documentos', roles: ['client'], clientRoles: ['lab_owner', 'client_admin'] },
    { path: '/my-employees', icon: UserCog, label: 'Empleados', roles: ['client'], clientRoles: ['lab_owner'] },
    { path: '/my-notes', icon: StickyNote, label: 'Mis Notas', roles: ['admin'] },
    { path: '/settings', icon: Settings, label: 'Configuración', roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (!item.roles.includes(user?.role || '')) return false;
    // For client users, check clientRoles if specified
    if (user?.role === 'client' && item.clientRoles) {
      return item.clientRoles.includes(user?.clientRole || 'lab_owner');
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`bg-gray-100 text-gray-800 flex flex-col fixed h-full border-r border-gray-300 transition-all duration-300 ease-in-out z-30 ${
        collapsed ? 'w-[70px]' : 'w-64'
      }`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-300 flex items-center justify-center relative" style={{ minHeight: '80px' }}>
          <Link to="/" className="flex items-center justify-center overflow-hidden">
            {collapsed ? (
              <img src="/logo.svg" alt="iB" className="h-10 w-10 object-contain" />
            ) : (
              <img src="/logo.svg" alt="iBilling" className="h-16 w-auto" />
            )}
          </Link>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-[90px] w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 hover:shadow transition-all z-40"
          title={collapsed ? 'Expandir' : 'Contraer'}
        >
          {collapsed ? <ChevronRight size={14} className="text-gray-500" /> : <ChevronLeft size={14} className="text-gray-500" />}
        </button>

        {/* Navigation */}
        <nav className={`flex-1 space-y-1 overflow-hidden ${collapsed ? 'px-2 py-4' : 'p-4'}`}>
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 rounded-lg transition whitespace-nowrap ${
                collapsed ? 'px-0 py-3 justify-center' : 'px-4 py-3'
              } ${
                isActive(item.path)
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
            >
              <item.icon size={20} className="flex-shrink-0" />
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className={`border-t border-gray-300 ${collapsed ? 'p-2' : 'p-4'}`}>
          {collapsed ? (
            <>
              <div className="flex justify-center mb-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white text-xs" title={`${user?.firstName} ${user?.lastName}`}>
                  {user?.initials || user?.firstName?.charAt(0)}
                </div>
              </div>
              <button
                onClick={logout}
                title="Cerrar Sesión"
                className="w-full flex items-center justify-center py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0">
                  {user?.initials || user?.firstName?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {user?.role === 'admin' ? 'Administrador' : 'Cliente'}
                  </p>
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition text-sm"
              >
                <LogOut size={18} />
                Cerrar Sesión
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${
        collapsed ? 'ml-[70px]' : 'ml-64'
      }`}>
        {/* Notifications - floating top right (clients only) */}
        {user?.role === 'client' && (
          <div className="flex items-center justify-end px-6 py-2">
            <NotificationBell />
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
